import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { DateTime } from 'luxon'
import { OnlineDentistKeys } from '../../models/enums'
import { AppDispatch, RootState } from '../../store'
import { Clinic } from '../../models/types'
import { setNearestAppointmentDate } from '../appointmentScreen/appointmentScreenSlice'
import { getTreatmentIdsFromTreatments } from '../treatmentScreen'
import {
  filterAndFetchClinics,
  transformClinicResponse,
  transformUpcomingAppointments,
} from './clinicUtils'
import { findClinicsByIds, Screens } from 'patient-api-lib'
import { growthbook } from '../../util/growthbook'
import { featureName } from '../../constants/app'

interface ClinicScreenState {
  pending: boolean
  clinics: Clinic[]
  selectedClinic: Clinic | null
}

export const initialState: ClinicScreenState = {
  pending: false,
  clinics: [],
  selectedClinic: null,
}

export const updateUpcomingAppointments = createAsyncThunk<
  Clinic['upcomingAppointments'],
  { clinicId: string; pageSize?: number },
  { dispatch: AppDispatch; state: RootState }
>('clinicScreen/updateUpcomingAppointments', async (payload, thunkAPI) => {
  const { clinicId, pageSize = 20 } = payload
  const state = thunkAPI.getState()
  const startDate = DateTime.now()
  const endDate = startDate.plus({ month: 3 })
  const selectedTreatment = state.treatmentScreen.selectedTreatment
  const treatmentIds = getTreatmentIdsFromTreatments(selectedTreatment)

  return await transformUpcomingAppointments(
    startDate,
    endDate,
    treatmentIds,
    clinicId,
    pageSize
  )
})

export const fetchClinicsWithoutCity = createAsyncThunk<
  Clinic[],
  void,
  { dispatch: AppDispatch; state: RootState }
>('clinicScreen/fetchClinicsWithoutCity', async (_, thunkAPI) => {
  const state = thunkAPI.getState()
  const selectedTreatment = state.treatmentScreen.selectedTreatment
  const selectedTreatmentIds = getTreatmentIdsFromTreatments(selectedTreatment)
  const startDate = DateTime.now()
  const endDate = startDate.plus({ month: 3 })

  if (!selectedTreatment) return []

  const filteredClinics = state.app.clinics.filter((c) =>
    c.treatmentIds.some((id) => selectedTreatmentIds.includes(id))
  )

  const filteredClinicsObject = await filterAndFetchClinics(
    startDate,
    endDate,
    filteredClinics,
    selectedTreatmentIds,
    200
  )

  return filteredClinicsObject
})

export const fetchClinics = createAsyncThunk<
  Clinic[],
  void,
  { dispatch: AppDispatch; state: RootState }
>('clinicScreen/fetchClinics', async (_, thunkAPI) => {
  const state = thunkAPI.getState()
  const city = state.cityScreen.city
  const clinics = state.app.clinics
  const latLng = city?.latLng
  const startDate = DateTime.now()
  const endDate = startDate.plus({ month: 3 })
  const selectedTreatment = state.treatmentScreen.selectedTreatment
  const selectedTreatmentIds = getTreatmentIdsFromTreatments(selectedTreatment)

  if (!latLng || !city || !selectedTreatment) return []

  const filteredClinics = clinics.filter((c) => {
    if (c.id === OnlineDentistKeys.id) {
      return c.treatmentIds.some((id) => selectedTreatmentIds.includes(id))
    }

    return (
      c.city === city.name &&
      c.treatmentIds.some((id) => selectedTreatmentIds.includes(id))
    )
  })

  const filteredClinicsObject = await filterAndFetchClinics(
    startDate,
    endDate,
    filteredClinics,
    selectedTreatmentIds,
    200
  )

  const filteredByCountUpcomingAppointments = filteredClinicsObject.filter(
    (c) =>
      c.upcomingAppointments !== undefined && c.upcomingAppointments.count > 0
  )

  return filteredByCountUpcomingAppointments
})

export const fetchClinic = createAsyncThunk<
  Clinic,
  string,
  { dispatch: AppDispatch; state: RootState }
>('clinicScreen/fetchClinic', async (clinicId, thunkApi) => {
  const findClinicsResponse = await findClinicsByIds([clinicId])

  if (!findClinicsResponse.getClinicsList().length) {
    throw new Error('Clinic not found')
  }

  const clinic = findClinicsResponse
    .getClinicsList()
    .map(transformClinicResponse)[0]

  if (!clinic) {
    return thunkApi.rejectWithValue('Clinic info not transformed')
  }

  return clinic
})

export const done = createAsyncThunk<
  Screens,
  void,
  { dispatch: AppDispatch; state: RootState }
>('clinicScreen/done', async (_, thunkApi) => {
  const state = thunkApi.getState()
  const selectedClinic = state.clinicScreen.selectedClinic
  const selectedAppointment = state.appointmentScreen.selectedAppointment

  thunkApi.dispatch(setNearestAppointmentDate(selectedClinic))

  if (selectedAppointment) {
    if (growthbook.isOn(featureName.bookingAlert)) {
      return Screens.BookingContactScreen
    }

    return Screens.BookingSummaryScreen
  }

  return Screens.AppointmentScreen
})

export const ClinicScreenSlice = createSlice({
  name: 'clinicScreen',
  initialState,
  reducers: {
    setClinics: (state, action: PayloadAction<Clinic[]>) => {
      state.clinics = action.payload
    },
    setSelectedClinic: (state, action: PayloadAction<Clinic | null>) => {
      state.selectedClinic = action.payload
    },
    clearState: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(fetchClinics.pending, (state) => {
      state.pending = true
    })
    builder.addCase(fetchClinics.fulfilled, (state, action) => {
      state.pending = false
      state.clinics = action.payload
    })
    builder.addCase(fetchClinicsWithoutCity.pending, (state) => {
      state.pending = true
    })
    builder.addCase(fetchClinicsWithoutCity.fulfilled, (state, action) => {
      state.pending = false
      state.clinics = action.payload
    })
    builder.addCase(fetchClinic.fulfilled, (state, action) => {
      state.clinics = [state.clinics, action.payload].flat()
    })
    builder.addCase(updateUpcomingAppointments.fulfilled, (state, action) => {
      if (!action.payload) return

      state.clinics = state.clinics.map((clinic) => {
        if (clinic.id === action.meta.arg.clinicId)
          return {
            ...clinic,
            upcomingAppointments: action.payload,
          }

        return clinic
      })

      if (
        state.selectedClinic &&
        state.selectedClinic.id === action.meta.arg.clinicId
      )
        state.selectedClinic = {
          ...state.selectedClinic,
          upcomingAppointments: action.payload,
        }
    })
  },
})

export const { setClinics, setSelectedClinic, clearState } =
  ClinicScreenSlice.actions

export const selectClinics = (state: RootState) => state.clinicScreen.clinics

export const selectSelectedClinic = (state: RootState) =>
  state.clinicScreen.selectedClinic

export const isPending = (state: RootState) => state.clinicScreen.pending

export const clinicScreenReducer = ClinicScreenSlice.reducer
